import type { LatLngExpression } from 'leaflet';
import type * as GeoJSON from 'geojson';

/**
 * Zone polygon geometry for Leaflet — shared by every layer that draws zone areas (main map's
 * neighbour polygons, Stressszenarien unit bubbles, Netzmonitoring Ampel map).
 */

/** GeoJSON [lng, lat] rings → Leaflet [lat, lng] rings; a MultiPolygon is flattened to its rings. */
export function polygonToLatLngs(polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon): LatLngExpression[][] {
  if (polygon.type === 'Polygon') {
    return polygon.coordinates.map(ring => ring.map(c => [c[1], c[0]] as LatLngExpression));
  }
  return polygon.coordinates.flatMap(poly => poly.map(ring => ring.map(c => [c[1], c[0]] as LatLngExpression)));
}

/** Plain vertex average — good enough to place a label inside a compact zone area. */
export function polygonCentroid(positions: LatLngExpression[][]): [number, number] {
  let lat = 0, lng = 0, n = 0;
  for (const ring of positions) {
    for (const p of ring) {
      const [a, b] = p as [number, number];
      lat += a; lng += b; n++;
    }
  }
  return n > 0 ? [lat / n, lng / n] : [0, 0];
}

/**
 * Indices of the points that belong to the main cluster — for fitting a map view to "the grid"
 * when single areas lie far away (a test zone in another town would shrink every other zone to
 * a dot). A point is an outlier when it is farther from the median centre than
 * `factor` × the 75th-percentile distance. Fewer than 5 points are never trimmed.
 */
export function mainClusterIndices(points: [number, number][], factor = 4): number[] {
  const all = points.map((_, i) => i);
  if (points.length < 5) return all;
  const median = (values: number[]) => {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  };
  const centreLat = median(points.map(p => p[0]));
  const centreLng = median(points.map(p => p[1]));
  // Equirectangular distance is plenty for an outlier test within one country
  const cosLat = Math.cos((centreLat * Math.PI) / 180);
  const distances = points.map(p => Math.hypot(p[0] - centreLat, (p[1] - centreLng) * cosLat));
  const sorted = [...distances].sort((a, b) => a - b);
  const p75 = sorted[Math.floor(sorted.length * 0.75)];
  if (p75 <= 0) return all;
  const kept = all.filter(i => distances[i] <= factor * p75);
  return kept.length > 0 ? kept : all;
}
